/**
 * Index contract:
 * - Assistant backend uses enumerate(citations, start=1) → ref.index is 1-based
 * - Proposed answer backend uses 0-based paragraph_indices (not handled here)
 * - The indexToId map uses ref.index as-is — callers must ensure references[].index
 *   matches the ref:N values embedded in the message text
 */

import { visit } from "unist-util-visit";
import { Node } from "unist";
import { Transformer } from "unified";
import * as React from "react";

interface TextNode extends Node {
  type: "text";
  value: string;
}

interface ElementNode extends Node {
  type: "element";
  tagName: string;
  properties: Record<string, unknown>;
  children: (TextNode | ElementNode)[];
}

export interface RehypeLinkifyOptions {
  /**
   * CSS class name for the reference link element.
   * Default: "inline-flex items-center px-1 py-0.5 text-xs font-medium mx-0.5 rounded-sm no-underline"
   */
  className?: string;
  /** Called when user hovers over/leaves a reference link. null = mouse left. */
  hoverCallback?: (referenceId: string | null) => void;
}

/** Minimal reference shape needed to resolve ref:N → reference ID */
export interface RehypeReference {
  id: string;
  index: number;
  type?: string;
}

export function getRefColorClasses(type?: string): string {
  switch (type) {
    case "SQL":
      return "text-emerald-700 bg-emerald-500/15 hover:bg-emerald-500/25 dark:text-emerald-400";
    case "MESSAGE":
      return "text-amber-700 bg-amber-500/15 hover:bg-amber-500/25 dark:text-amber-400";
    case "EXTERNAL_TICKET":
      return "text-violet-700 bg-violet-500/15 hover:bg-violet-500/25 dark:text-violet-400";
    case "MEMORY":
      return "text-purple-700 bg-purple-500/15 hover:bg-purple-500/25 dark:text-purple-400";
    case "FACT":
      // Sparkling Purple — matches the "Eigene Fakten" notebook accent so the
      // citation badge for an agent-contributed fact reads as the same kind of
      // thing as the EIGENE FAKTEN section it lives in.
      return "text-accent bg-accent/15 hover:bg-accent/25";
    case "DOCUMENT":
    default:
      return "text-primary bg-primary/10 hover:bg-primary/20";
  }
}

const DEFAULT_CLASS_NAME =
  "inline-flex items-center px-1 py-0.5 text-xs font-medium mx-0.5 rounded-sm no-underline";

/** Hexagonal citation badge className used by proposed answers and assistant chat. */
export const CITATION_CLASSNAME =
  "inline-flex items-center justify-center w-5 h-5 text-xs transition-all duration-150 font-medium mx-0.5 no-underline cursor-pointer [clip-path:polygon(25%_0%,75%_0%,100%_50%,75%_100%,25%_100%,0%_50%)]";

/**
 * Rehype plugin that transforms source reference patterns in AI-generated text into clickable links.
 *
 * Supports two formats:
 * - Current: `[ref:1, ref:2]` or standalone `ref:1` (index-based, resolved via references list).
 *   This is the ONLY format produced by current backend pipelines.
 * - Legacy: `[1:uuid, 2:uuid]` or standalone `1:uuid` (UUID embedded in text).
 *   NO LONGER PRODUCED — handled only to render old stored messages from before
 *   the format switch. `stripCitationMarkers` in ./utils.ts mirrors this; remove
 *   the legacy branch in both places together once old messages age out.
 *
 * @param messageId - The ID of the message containing references
 * @param callback - Function called when a reference link is clicked
 * @param options - Optional configuration for styling
 * @param references - List of references to resolve index-based citations
 */
export const rehypeLinkifySources = (
  messageId: string,
  callback: (messageId: string, referenceId: string) => void,
  options?: RehypeLinkifyOptions,
  references?: RehypeReference[],
): Transformer => {
  const className = options?.className ?? DEFAULT_CLASS_NAME;
  const hoverCallback = options?.hoverCallback;

  // Build index→id lookup for new format refs
  const indexToId = new Map<number, string>();
  const indexToType = new Map<number, string | undefined>();
  if (references) {
    for (const ref of references) {
      indexToId.set(ref.index, ref.id);
      indexToType.set(ref.index, ref.type);
    }
  }

  return (tree: Node) => {
    visit(
      tree,
      "text",
      (node: TextNode, index, parent: ElementNode | undefined) => {
        if (parent?.tagName === "a") return;

        const text = node.value;

        // New format: ref:N (whitespace after "ref:" tolerated, e.g. "ref: 1")
        const newRefPattern = /ref:\s*(\d+)/g;
        // New format bracketed: [ref:1, ref:2]. The "ref:" prefix is optional on
        // indices after the first ("[ref:1, 2]") since the LLM sometimes drops it,
        // and whitespace after "ref:" is tolerated ("[ref: 1]").
        const newBracketedPattern = /\[(ref:\s*\d+(?:\s*,\s*(?:ref:\s*)?\d+)*)\]/g;

        // Legacy format: digit:uuid
        const legacyRefPattern =
          /(\d+):\b([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\b/g;
        // Legacy format bracketed
        const legacyBracketedPattern =
          /\[((?:\d+:\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\b)(?:\s*,\s*(?:\d+:\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\b))*)\]/g;

        // Combined pattern to process all formats in order of appearance
        const combinedPattern = new RegExp(
          `${newBracketedPattern.source}|${legacyBracketedPattern.source}|${newRefPattern.source}|${legacyRefPattern.source}`,
          "g",
        );

        let lastIndex = 0;
        const newChildren: (TextNode | ElementNode)[] = [];
        let match;

        while ((match = combinedPattern.exec(text)) !== null) {
          // Add text before this match
          if (match.index > lastIndex) {
            newChildren.push({
              type: "text",
              value: text.slice(lastIndex, match.index),
            } as TextNode);
          }

          if (match[1] !== undefined) {
            // New bracketed: [ref:1, ref:2]
            const entries = match[1].split(",").map((e) => e.trim());
            for (let i = 0; i < entries.length; i++) {
              const entryMatch = /(?:ref:)?(\d+)/.exec(entries[i]);
              if (entryMatch) {
                const refIdx = parseInt(entryMatch[1], 10);
                const refId = indexToId.get(refIdx);
                if (refId) {
                  if (i > 0) {
                    newChildren.push({ type: "text", value: " " } as TextNode);
                  }
                  newChildren.push(
                    createReferenceLink(
                      String(refIdx),
                      refId,
                      messageId,
                      callback,
                      `${className} ${getRefColorClasses(indexToType.get(refIdx))}`,
                      hoverCallback,
                    ),
                  );
                }
              }
            }
          } else if (match[2] !== undefined) {
            // Legacy bracketed: [1:uuid, 2:uuid]
            const entries = match[2].split(",").map((e) => e.trim());
            for (let i = 0; i < entries.length; i++) {
              const entryMatch =
                /(\d+):(\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\b)/.exec(
                  entries[i],
                );
              if (entryMatch) {
                if (i > 0) {
                  newChildren.push({ type: "text", value: " " } as TextNode);
                }
                newChildren.push(
                  createReferenceLink(
                    entryMatch[1],
                    entryMatch[2],
                    messageId,
                    callback,
                    `${className} ${getRefColorClasses()}`,
                    hoverCallback,
                  ),
                );
              }
            }
          } else if (match[3] !== undefined) {
            // New standalone: ref:N
            const refIdx = parseInt(match[3], 10);
            const refId = indexToId.get(refIdx);
            if (refId) {
              newChildren.push(
                createReferenceLink(
                  String(refIdx),
                  refId,
                  messageId,
                  callback,
                  `${className} ${getRefColorClasses(indexToType.get(refIdx))}`,
                  hoverCallback,
                ),
              );
            }
          } else if (match[4] !== undefined && match[5] !== undefined) {
            // Legacy standalone: N:uuid
            newChildren.push(
              createReferenceLink(
                match[4],
                match[5],
                messageId,
                callback,
                `${className} ${getRefColorClasses()}`,
                hoverCallback,
              ),
            );
          }

          lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < text.length) {
          newChildren.push({
            type: "text",
            value: text.slice(lastIndex),
          } as TextNode);
        }

        // If we made any changes, replace the original node
        if (
          newChildren.length > 0 &&
          lastIndex > 0 &&
          parent &&
          typeof index === "number"
        ) {
          parent.children.splice(index, 1, ...newChildren);
        }
      },
    );
  };
};

// Helper function to create a reference link element
function createReferenceLink(
  refIndex: string,
  referenceId: string,
  messageId: string,
  callback: (messageId: string, referenceId: string) => void,
  className: string,
  hoverCallback?: (referenceId: string | null) => void,
): ElementNode {
  return {
    type: "element",
    tagName: "a",
    properties: {
      href: `#`,
      "data-citation-ref": referenceId,
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        callback(messageId, referenceId);
      },
      onMouseEnter: hoverCallback ? () => hoverCallback(referenceId) : undefined,
      onMouseLeave: hoverCallback ? () => hoverCallback(null) : undefined,
      className,
      // user-select: none keeps the badge index out of manual (Ctrl+C) copies —
      // browsers exclude unselectable content from the clipboard. The copy
      // button strips markers separately (see stripCitationMarkers in ./utils.ts);
      // this covers the case where the user selects answer text by hand.
      // -webkit- prefix is required for the Outlook add-in's macOS WebKit webview.
      style: "text-decoration: none; -webkit-user-select: none; user-select: none;",
    },
    children: [
      {
        type: "text",
        value: `${refIndex}`,
      } as TextNode,
    ],
  } as ElementNode;
}
